import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Lock, Sparkles, ArrowRight, FileText, Bell, Users } from "lucide-react";
import { promoLanding } from "../lib/api";
import { Seo } from "../components/Seo";

const Feature = ({ icon: Icon, title, body }) => (
  <div className="flex gap-3">
    <span className="h-9 w-9 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center"><Icon className="h-4 w-4" /></span>
    <div><div className="font-medium text-sm">{title}</div><div className="text-sm text-muted-foreground">{body}</div></div>
  </div>
);

export default function PromoLanding() {
  const { code } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["promo", code], queryFn: () => promoLanding(code), retry: false });

  useEffect(() => { if (data?.code) document.title = `HearthVault — ${data.creator_name || "welcome"}`; }, [data]);

  if (isLoading) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="promo-loading">Loading…</div>;

  if (isError || !data) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center" data-testid="promo-notfound">
        <div>
          <h1 className="font-serif text-3xl mb-2">That link isn't active</h1>
          <p className="text-muted-foreground mb-6">This invite code is unknown or paused — but you can still start your vault.</p>
          <Link to="/welcome" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3">Start your vault <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    );
  }

  const start = `/welcome?promo=${encodeURIComponent(data.code)}&src=creator`;
  return (
    <div className="min-h-screen bg-background" data-testid="promo-landing">
      <Seo title={`HearthVault — recommended by ${data.creator_name || "a friend"}`} description="The private, zero-tracking vault for your family's important documents and estate plan." noindex />
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground mb-6">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Zero-tracking · your keys, not ours
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">Everything your family would need — in one calm, private place.</h1>
        <p className="mt-5 text-base text-muted-foreground max-w-xl">
          {data.creator_name ? <><b className="text-foreground">{data.creator_name}</b> sent you here. </> : null}
          HearthVault keeps your wills, insurance, accounts, warranties and passwords organized, and ready for the people who'll need them.
        </p>

        {data.discount_percent > 0 && (
          <div data-testid="promo-discount-badge" className="mt-6 inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-emerald-900">
            <Sparkles className="h-4 w-4" /> <span className="text-sm"><b>{data.discount_percent}% off</b> with code <b className="font-mono">{data.code}</b> — applied automatically at checkout.</span>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to={start} data-testid="promo-cta" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-base hover:opacity-90 transition-opacity">
            Start your free vault <ArrowRight className="h-4 w-4" />
          </Link>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> No tracking. Cancel anytime.</span>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          <Feature icon={FileText} title="AI auto-filing" body="Drop in a document and it's named, summarized and filed for you." />
          <Feature icon={Bell} title="Smart reminders" body="Renewals, expirations and reviews never slip by again." />
          <Feature icon={Users} title="Break-glass access" body="Name an executor who can reach what matters, only when it's time." />
          <Feature icon={ShieldCheck} title="Truly private" body="Encrypted at rest, never sold, never used to train AI." />
        </div>
      </div>
    </div>
  );
}
